/**
 * service-mail supervisor-approvals client. Mirrors:
 *   apps/service-mail/src/modules/supervisor-approvals/supervisor-approvals.controller.ts
 *
 * RBAC:
 *   - create requires Mail/requestApproval (any operator).
 *   - list, approve, reject require Mail/approveSupervisorRequest (supervisor).
 */
import { env } from '../env';

import { request } from './client';

export type SupervisorApprovalKind = 'WEIGHT_ADJUSTMENT' | 'REFUND' | 'STATUS_OVERRIDE' | 'OTHER';
export type SupervisorApprovalSubjectType = 'MAIL_ITEM' | 'TXN' | 'OTHER';
export type SupervisorApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SupervisorApprovalDto {
  readonly id: string;
  readonly kind: SupervisorApprovalKind;
  readonly requestedByOperatorId: string;
  readonly requestedAtOutletCode: string | null;
  readonly subjectType: SupervisorApprovalSubjectType;
  readonly subjectId: string | null;
  readonly summary: string;
  readonly payload: unknown;
  readonly status: SupervisorApprovalStatus;
  readonly decidedBySupervisorId: string | null;
  readonly decidedAt: string | null;
  readonly decisionReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListSupervisorApprovalsResponse {
  readonly items: readonly SupervisorApprovalDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface CreateApprovalBody {
  readonly kind: SupervisorApprovalKind;
  readonly subjectType: SupervisorApprovalSubjectType;
  readonly subjectId?: string;
  readonly summary: string;
  readonly payload: unknown;
  readonly outletCode?: string;
}

export interface ListApprovalsQuery {
  readonly status?: SupervisorApprovalStatus;
  readonly kind?: SupervisorApprovalKind;
  readonly page?: number;
  readonly pageSize?: number;
}

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function createSupervisorApproval(body: CreateApprovalBody): Promise<SupervisorApprovalDto> {
  return request<SupervisorApprovalDto>(baseUrl(), '/v1/supervisor-approvals', { method: 'POST', body });
}

export async function listSupervisorApprovals(query: ListApprovalsQuery = {}): Promise<ListSupervisorApprovalsResponse> {
  return request<ListSupervisorApprovalsResponse>(baseUrl(), '/v1/supervisor-approvals', {
    method: 'GET',
    query: {
      status: query.status,
      kind: query.kind,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function approveSupervisorApproval(id: string, reason?: string): Promise<SupervisorApprovalDto> {
  return request<SupervisorApprovalDto>(baseUrl(), `/v1/supervisor-approvals/${id}/approve`, {
    method: 'POST',
    body: reason !== undefined ? { reason } : {},
  });
}

export async function rejectSupervisorApproval(id: string, reason: string): Promise<SupervisorApprovalDto> {
  return request<SupervisorApprovalDto>(baseUrl(), `/v1/supervisor-approvals/${id}/reject`, {
    method: 'POST',
    body: { reason },
  });
}
